import { useEffect, useRef } from "react";

import { formatDate } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { CircularLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { AI_AGENT_MEETING } from "app/pages/sales-intelligence/constants/routes";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import type { Thread } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";
import {
    StyledEmptyState,
    StyledEmptyStateIcon,
    StyledHistoryDDContainer,
    StyledHistoryDDItem,
    StyledHistoryDDItemInner,
    StyledHistoryDDItemsContainer,
    StyledHistoryDDItemTitle,
} from "app/pages/sales-intelligence/pages/ai-agent/components/HistoryDropdown/styles";
import { proNavigator } from "single-spa/react-app/ProNavigator";

type Props = {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    data: Thread[] | undefined;
    currentThreadId: string | undefined;
    isLoading?: boolean;
};

export const HistoryDropdown = ({ isOpen, setIsOpen, data, currentThreadId, isLoading = false }: Props) => {
    const dropdownRef = useRef<HTMLDivElement>(null);

    const handleDDItemClick = (thread: Thread) => {
        if (thread.threadId === currentThreadId) {
            setIsOpen(false);
            return;
        }
        AIAgentTrackingService.meeting.trackOpenHistoryChat(thread.companyName);
        void proNavigator.go(
            AI_AGENT_MEETING,
            {
                id: thread.threadId,
                domain: thread.domain,
            },
            { inherit: false },
        );
    };

    useEffect(() => {
        const handleBodyClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        document.body.addEventListener("click", handleBodyClick, { capture: true });

        return () => {
            document.body.removeEventListener("click", handleBodyClick, { capture: true });
        };
    }, [setIsOpen]);

    if (!isOpen) {
        return null;
    }

    return (
        <StyledHistoryDDContainer ref={dropdownRef}>
            <Text
                fontVariant="h6"
                color={colorsPalettes.carbon[500]}
                paddingX="8px"
            >
                {translate("si.pages.ai_agent.actions.history.tooltip")} ({data?.length ?? 0})
            </Text>
            <StyledHistoryDDItemsContainer>
                {isLoading ? (
                    <Flex
                        justifyContent="center"
                        alignItems="center"
                        paddingY="44px"
                        width="100%"
                        data-automation="ai-agent-history-dropdown-loading"
                    >
                        <CircularLoader isLarge />
                    </Flex>
                ) : data?.length ? (
                    data.map((thread) => (
                        <StyledHistoryDDItem
                            key={thread.threadId}
                            isActive={thread.threadId === currentThreadId}
                            onClick={() => handleDDItemClick(thread)}
                        >
                            <StyledHistoryDDItemInner>
                                <StyledHistoryDDItemTitle>
                                    {translate("si.pages.ai_agent.action.history_item.text", {
                                        companyName: thread.companyName,
                                    })}
                                </StyledHistoryDDItemTitle>
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[300]}
                                    margin="4px 0 0"
                                    display="block"
                                >
                                    {formatDate(thread.createdDate, undefined, "MMM DD, YYYY")}
                                </Text>
                            </StyledHistoryDDItemInner>
                        </StyledHistoryDDItem>
                    ))
                ) : (
                    <StyledEmptyState>
                        <StyledEmptyStateIcon iconName="no-data-lab" />
                        <Text
                            fontVariant="subtitle1"
                            color={colorsPalettes.carbon[500]}
                        >
                            {translate("si.pages.ai_agent.actions.history.empty_state.text")}
                        </Text>
                    </StyledEmptyState>
                )}
            </StyledHistoryDDItemsContainer>
        </StyledHistoryDDContainer>
    );
};
